import React from 'react';
import { X } from 'lucide-react';

interface FilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    filters: {
        frameworks: string[];
        autonomyLevels: number[];
        openSourceStatus: boolean | null;
        marketCapMin: string;
        marketCapMax: string;
    };
    onFilterChange: (newFilters: any) => void;
    agents: Array<any>;
}

const FilterModal: React.FC<FilterModalProps> = ({
    isOpen,
    onClose,
    filters,
    onFilterChange,
    agents
}) => {
    
    // Extract unique frameworks from agents data
    const uniqueFrameworks = React.useMemo(() => {
        const frameworks = agents
            .map(agent => agent.framework)
            .filter(Boolean) // Remove null/undefined values
            .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
            .sort(); // Sort alphabetically

            return frameworks;
        }, [agents]);
        
    if (!isOpen) return null;
    const handleFrameworkToggle = (framework: string) => {
        const updatedFrameworks = filters.frameworks.includes(framework)
            ? filters.frameworks.filter(f => f !== framework)
            : [...filters.frameworks, framework];

        onFilterChange({ ...filters, frameworks: updatedFrameworks });
    };

    const handleAutonomyLevelToggle = (level: number) => {
        const updatedLevels = filters.autonomyLevels.includes(level)
            ? filters.autonomyLevels.filter(l => l !== level)
            : [...filters.autonomyLevels, level];

        onFilterChange({ ...filters, autonomyLevels: updatedLevels });
    };

    const resetFilters = () => {
        onFilterChange({
            frameworks: [],
            autonomyLevels: [],
            openSourceStatus: null,
            marketCapMin: '',
            marketCapMax: ''
        });
    };

    // Prevent clicks inside modal from closing it
    const handleModalClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-[#1A1721] rounded-xl w-full max-w-xl overflow-y-auto max-h-[90vh]"
                onClick={handleModalClick}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-800 flex justify-between items-center sticky top-0 bg-[#1A1721]">
                    <h3 className="text-xl text-white font-semibold">Filters</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Filter Content */}
                <div className="p-6 space-y-6">
                    {/* Framework Filter */}
                    <div>
                        <h4 className="text-white mb-3">Framework</h4>
                        <div className="flex flex-wrap gap-2">
                            {uniqueFrameworks.map(framework => (
                                <button
                                    key={framework}
                                    onClick={() => handleFrameworkToggle(framework)}
                                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${filters.frameworks.includes(framework)
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                        }`}
                                >
                                    {framework}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Autonomy Level Filter */}
                    <div>
                        <h4 className="text-white mb-3">Level of Autonomy</h4>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { value: 1, label: 'LOW' },
                                { value: 2, label: 'MEDIUM' },
                                { value: 3, label: 'HIGH' },
                                { value: 4, label: 'FULL' }
                            ].map(({ value, label }) => (
                                <button
                                    key={value}
                                    onClick={() => handleAutonomyLevelToggle(value)}
                                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${filters.autonomyLevels.includes(value)
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                        }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Market Cap Range */}
                    <div>
                        <h4 className="text-white mb-3">Market Cap Range</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <input
                                type="number"
                                placeholder="Min"
                                value={filters.marketCapMin}
                                onChange={(e) => onFilterChange({
                                    ...filters,
                                    marketCapMin: e.target.value
                                })}
                                className="px-3 py-2 bg-gray-800 rounded-lg text-white border border-gray-700 focus:border-purple-500 focus:outline-none"
                            />
                            <input
                                type="number"
                                placeholder="Max"
                                value={filters.marketCapMax}
                                onChange={(e) => onFilterChange({
                                    ...filters,
                                    marketCapMax: e.target.value
                                })}
                                className="px-3 py-2 bg-gray-800 rounded-lg text-white border border-gray-700 focus:border-purple-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    {/* Open Source Filter */}
                    <div>
                        <h4 className="text-white mb-3">Source Status</h4>
                        <div className="flex gap-2">
                            <button
                                onClick={() => onFilterChange({
                                    ...filters,
                                    openSourceStatus: filters.openSourceStatus === true ? null : true
                                })}
                                className={`px-4 py-2 rounded-lg text-sm transition-colors ${filters.openSourceStatus === true
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                    }`}
                            >
                                Open Source
                            </button>
                            <button
                                onClick={() => onFilterChange({
                                    ...filters,
                                    openSourceStatus: filters.openSourceStatus === false ? null : false
                                })}
                                className={`px-4 py-2 rounded-lg text-sm transition-colors ${filters.openSourceStatus === false
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                    }`}
                            >
                                Closed Source
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-6 border-t border-gray-800 sticky bottom-0 bg-[#1A1721]">
                    <div className="flex gap-3">
                        <button
                            onClick={resetFilters}
                            className="px-4 py-2 rounded-lg text-sm bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                        >
                            Reset Filters
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-sm bg-purple-600 text-white hover:bg-purple-700 transition-colors flex-1"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterModal;